import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ArrowLeft, BookOpen, Network } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import {
  OntologyManager,
  type OntologyFieldDraft,
  OntologyField,
  OntologyShot,
} from "@app/components/OntologyManager";
import OntologyStackManager, {
  OntologyStackConfig,
} from "@app/components/OntologyStackManager";
import API from "@app/services/api";
import { useViewAsKey } from "@app/services/view-as";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import { useReadonlyScope } from "@app/utils/use-readonly-scope";
import { Trans } from "@lingui/macro";

function parseFields(raw: unknown[]): OntologyField[] {
  return (
    raw as Array<{
      id: string;
      name: string;
      description: string | null;
      summary: string | null;
      field_type: string;
      output_constraints: Record<string, unknown> | null;
      field_key: string | null;
      group_name: string | null;
      group_emoji: string | null;
      evaluator_type: string | null;
      weight: number;
      pending_proposals?: number;
      shots: Array<{
        id: string;
        input: string;
        output: string | Record<string, unknown>;
      }>;
    }>
  ).map((f) => ({
    id: f.id,
    name: f.name,
    description: f.description || "",
    summary: f.summary || "",
    fieldType: f.field_type as "patient" | "content" | "metrics",
    outputConstraints: f.output_constraints || {},
    fieldKey: f.field_key,
    groupName: f.group_name,
    groupEmoji: f.group_emoji,
    evaluatorType: f.evaluator_type,
    weight: f.weight ?? 1,
    pendingProposals: f.pending_proposals ?? 0,
    shots: f.shots.map((s) => ({
      id: s.id,
      input: s.input,
      output: s.output,
    })),
  }));
}

export default function LaboratoireOntologiePage() {
  const labReadOnly = useLabReadOnly();
  /**
   * Une spécialité empruntée se lit et ne s'édite pas (PT-74). Ces champs SONT l'ontologie vivante
   * de la spécialité — le backend refuse désormais l'écriture, ici comme sur les dossiers, et un
   * bouton qui répond 403 est pire qu'un bouton absent.
   */
  const readonlyScope = useReadonlyScope();
  const readOnly = labReadOnly || readonlyScope;
  /** Whose platform is on screen — every read below answers for them. */
  const viewAsKey = useViewAsKey();

  const [stacks, setStacks] = useState<OntologyStackConfig[]>([]);
  const [selectedStackId, setSelectedStackId] = useState<string | null>(null);
  // Atterrissage direct sur un champ, depuis le classement des corrections.
  //
  // `/laboratoire/ontologie?stack=<id>&field=<id>` ouvre la configuration ET déplie les
  // retours du champ visé. C'est ce qui fait du classement « quels champs sont repris » un
  // point de départ plutôt qu'un constat : on y lit QUEL champ reprendre, on arrive ici sur
  // CE champ, avec sous les yeux les valeurs écrites par les médecins et leurs extraits.
  const [searchParams] = useSearchParams();
  const focusStackId = searchParams.get("stack");
  const focusFieldId = searchParams.get("field");
  const [selectedStackTitle, setSelectedStackTitle] = useState<string>("");
  const [fields, setFields] = useState<OntologyField[]>([]);
  const [userSpecialite, setUserSpecialite] = useState<string | null>(null);
  const [deployingStackId, setDeployingStackId] = useState<string | null>(null);

  const loadStacks = useCallback(async () => {
    const response = await API.get({ path: "/ontology-stack/" });
    if (response.ok && Array.isArray(response.data)) {
      setStacks(response.data as OntologyStackConfig[]);
    }
  }, []);

  const loadStackFields = useCallback(async (stackId: string) => {
    const response = await API.get({
      path: `/ontology-stack/${stackId}/fields`,
    });
    if (response.ok && response.data?.fields) {
      setFields(parseFields(response.data.fields));
    }
  }, []);

  const loadUserSpecialite = useCallback(async () => {
    try {
      const res = await API.get({ path: "/configuration/" });
      if (res.ok && res.data?.specialite) {
        setUserSpecialite(res.data.specialite);
      }
    } catch {
      // Specialite unavailable — deploy button simply won't render.
    }
  }, []);

  // Refetched when the admin switches « Vue spécialité ». Both calls depend on it, and the second
  // one is the dangerous half: `userSpecialite` is the TARGET of the deploy button, so a stale
  // value writes one speciality's field definitions onto another's live ontology.
  useEffect(() => {
    void loadStacks();
    void loadUserSpecialite();
  }, [loadStacks, loadUserSpecialite, viewAsKey]);

  // Ouvre la configuration visée par l'URL. Posé une fois, et non à chaque rendu : le
  // lecteur doit pouvoir refermer la modale sans qu'elle se rouvre aussitôt.
  useEffect(() => {
    if (focusStackId) setSelectedStackId(focusStackId);
  }, [focusStackId]);

  const handleAddStack = async (data: {
    title: string;
    comment: string | null;
  }) => {
    const response = await API.post({ path: "/ontology-stack/", body: data });
    if (response.ok) await loadStacks();
  };

  const handleUpdateStack = async (
    stackId: string,
    data: { title?: string; comment?: string | null },
  ) => {
    const response = await API.put({
      path: `/ontology-stack/${stackId}`,
      body: data,
    });
    if (response.ok) await loadStacks();
  };

  const handleDeleteStack = async (stackId: string) => {
    const response = await API.delete({ path: `/ontology-stack/${stackId}` });
    if (response.ok) {
      if (selectedStackId === stackId) {
        setSelectedStackId(null);
        setFields([]);
      }
      await loadStacks();
    }
  };

  const handleSelectStack = async (stackId: string) => {
    const stack = stacks.find((s) => s.id === stackId);
    setSelectedStackId(stackId);
    setSelectedStackTitle(stack?.title || "");
    await loadStackFields(stackId);
  };

  const handleCloseFieldsDialog = () => {
    setSelectedStackId(null);
    setSelectedStackTitle("");
    setFields([]);
  };

  const handleDeployToSpecialite = async (stackId: string) => {
    if (!userSpecialite) return;
    setDeployingStackId(stackId);
    try {
      await API.post({
        path: `/ontology-stack/${stackId}/deploy-to-specialite?specialite=${encodeURIComponent(userSpecialite)}`,
        body: {},
      });
    } finally {
      setDeployingStackId(null);
    }
  };

  // Field CRUD — scoped to the selected stack.
  const handleAddField = async (field: OntologyFieldDraft) => {
    if (!selectedStackId) return;
    const response = await API.post({
      path: `/ontology-stack/${selectedStackId}/fields`,
      body: {
        name: field.name,
        description: field.description,
        summary: field.summary,
        field_type: field.fieldType,
        output_constraints: field.outputConstraints || {},
        field_key: field.fieldKey,
        group_name: field.groupName,
        group_emoji: field.groupEmoji,
        evaluator_type: field.evaluatorType,
        weight: field.weight,
      },
    });
    if (response.ok) {
      await loadStackFields(selectedStackId);
      await loadStacks();
    }
  };

  const handleUpdateField = async (
    fieldId: string,
    updates: Partial<OntologyField>,
  ) => {
    if (!selectedStackId) return;
    const response = await API.put({
      path: `/ontology-stack/${selectedStackId}/fields/${fieldId}`,
      body: {
        name: updates.name,
        description: updates.description,
        summary: updates.summary,
        field_type: updates.fieldType,
        output_constraints: updates.outputConstraints,
        field_key: updates.fieldKey,
        group_name: updates.groupName,
        group_emoji: updates.groupEmoji,
        evaluator_type: updates.evaluatorType,
        weight: updates.weight,
      },
    });
    if (response.ok) await loadStackFields(selectedStackId);
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!selectedStackId) return;
    const response = await API.delete({
      path: `/ontology-stack/${selectedStackId}/fields/${fieldId}`,
    });
    if (response.ok) {
      await loadStackFields(selectedStackId);
      await loadStacks();
    }
  };

  const handleAddShot = async (
    fieldId: string,
    shot: Omit<OntologyShot, "id">,
  ) => {
    if (!selectedStackId) return;
    const response = await API.post({
      path: `/ontology-stack/${selectedStackId}/fields/${fieldId}/shots`,
      body: { input: shot.input, output: shot.output },
    });
    if (response.ok) await loadStackFields(selectedStackId);
  };

  const handleUpdateShot = async (
    fieldId: string,
    shotId: string,
    updates: Partial<OntologyShot>,
  ) => {
    if (!selectedStackId) return;
    const response = await API.put({
      path: `/ontology-stack/${selectedStackId}/fields/${fieldId}/shots/${shotId}`,
      body: { input: updates.input, output: updates.output },
    });
    if (response.ok) await loadStackFields(selectedStackId);
  };

  const handleDeleteShot = async (fieldId: string, shotId: string) => {
    if (!selectedStackId) return;
    const response = await API.delete({
      path: `/ontology-stack/${selectedStackId}/fields/${fieldId}/shots/${shotId}`,
    });
    if (response.ok) await loadStackFields(selectedStackId);
  };

  return (
    <div className="p-6">
      <Link
        to="/laboratoire"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        <Trans>Laboratoire</Trans>
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Network className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Trans>Ontologie</Trans>
          </h1>
          <p className="text-slate-600 mt-1">
            <Trans>
              Configurations d'ontologie réutilisables par toutes vos expériences.
            </Trans>
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            <Trans>Configurations</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <OntologyStackManager
            stacks={stacks}
            onAdd={handleAddStack}
            onUpdate={handleUpdateStack}
            onDelete={handleDeleteStack}
            onSelectStack={handleSelectStack}
            onDeployToSpecialite={
              userSpecialite && !readOnly ? handleDeployToSpecialite : undefined
            }
            deployingStackId={deployingStackId}
            specialite={userSpecialite ?? undefined}
            readOnly={readOnly}
          />
        </CardContent>
      </Card>

      <Dialog
        open={selectedStackId !== null}
        onOpenChange={(open) => {
          if (!open) handleCloseFieldsDialog();
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <DialogTitle>
                <Trans>Champs — {selectedStackTitle}</Trans>
              </DialogTitle>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              <Trans>
                Gérez les champs et exemples few-shot de cette configuration.
              </Trans>
            </p>
          </DialogHeader>
          {selectedStackId && (
            <OntologyManager
              fields={fields}
              onAddField={handleAddField}
              onUpdateField={handleUpdateField}
              onDeleteField={handleDeleteField}
              onAddShot={handleAddShot}
              onUpdateShot={handleUpdateShot}
              onDeleteShot={handleDeleteShot}
              onDeployToSpecialite={
                userSpecialite && !readOnly
                  ? () => handleDeployToSpecialite(selectedStackId)
                  : undefined
              }
              deployingToSpecialite={deployingStackId === selectedStackId}
              specialite={userSpecialite ?? undefined}
              stackId={selectedStackId}
              focusFieldId={selectedStackId === focusStackId ? focusFieldId : null}
              // Une proposition promue crée un exemple : la liste des champs doit repartir du
              // serveur, sinon le compteur « Exemples (n) » du champ resterait sur l'ancien.
              onShotsChanged={() => void loadStackFields(selectedStackId)}
              readOnly={readOnly}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
